import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Check, Sparkles, Clock, ArrowRight, Phone, GraduationCap, BookOpen,
  Calculator, Leaf, HeartHandshake, Palette, Backpack, Target,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROGRAM_STYLES = {
  toddler: {
    image: "https://images.unsplash.com/photo-1588075592446-265fd1e6e76f?w=600&q=80",
    icon: Sparkles,
    card: "bg-emerald-50 border-emerald-200",
    iconBox: "bg-emerald-100 text-emerald-700",
    check: "bg-emerald-200 text-emerald-700",
    badge: "bg-emerald-100 text-emerald-800",
  },
  preschool: {
    image: "https://images.pexels.com/photos/8363559/pexels-photo-8363559.jpeg?auto=compress&cs=tinysrgb&w=940",
    icon: GraduationCap,
    card: "bg-sky-50 border-sky-200",
    iconBox: "bg-sky-100 text-sky-700",
    check: "bg-sky-200 text-sky-700",
    badge: "bg-sky-100 text-sky-800",
  },
};

const CURRICULUM = [
  { icon: BookOpen, title: "Language and Literacy", text: "Recognize letters and letter sounds; practice writing names and letters; listen to stories and build vocabulary.", color: "bg-pink-100 text-pink-600 border-pink-200" },
  { icon: Calculator, title: "Mathematics", text: "Count from 1-20 and beyond; recognize numbers, shapes, and colors; explore patterns, sorting, and simple addition.", color: "bg-blue-100 text-blue-600 border-blue-200" },
  { icon: Leaf, title: "Science and Discovery", text: "Learn about animals, plants, weather, and seasons; explore nature through hands-on activities.", color: "bg-emerald-100 text-emerald-600 border-emerald-200" },
  { icon: HeartHandshake, title: "Social and Emotional Development", text: "Practice sharing, kindness, and taking turns; identify feelings, build friendships, and develop confidence.", color: "bg-orange-100 text-orange-600 border-orange-200" },
  { icon: Palette, title: "Art, Music, and Movement", text: "Draw, paint, sing, dance, create crafts, and participate in indoor and outdoor physical activities.", color: "bg-purple-100 text-purple-600 border-purple-200" },
  { icon: Backpack, title: "Life Skills & Kindergarten Readiness", text: "Follow directions and routines; practice handwashing and cleaning up; develop listening, problem-solving, and self-help skills.", color: "bg-yellow-100 text-yellow-700 border-yellow-200" },
];

const ProgramDetail = ({ program, reverse }) => {
  const s = PROGRAM_STYLES[program.id] || PROGRAM_STYLES.toddler;
  const Icon = s.icon;
  return (
    <div className="grid lg:grid-cols-2 gap-12 items-center" data-testid={`program-detail-${program.id}`}>
      <div className={`animate-fade-in ${reverse ? "lg:order-2" : ""}`}>
        <div className="relative img-zoom rounded-3xl overflow-hidden shadow-xl">
          <img src={s.image} alt={program.name} className="w-full h-96 object-cover" />
          <div className={`absolute top-4 left-4 ${s.badge} px-4 py-2 rounded-full text-sm font-medium`}>{program.age_range}</div>
        </div>
      </div>
      <div className="animate-fade-in-delay-2">
        <Card className={`${s.card} border-2 rounded-3xl`}>
          <CardContent className="p-8 lg:p-10">
            <div className={`w-14 h-14 ${s.iconBox} rounded-2xl flex items-center justify-center mb-6`}>
              <Icon className="w-7 h-7" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">{program.name}</h2>
            <p className="text-slate-700 mb-8 text-lg leading-relaxed">{program.description}</p>
            <div className="mb-8">
              <h3 className="font-semibold text-slate-900 mb-4">What's Included:</h3>
              <ul className="grid sm:grid-cols-2 gap-3">
                {program.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className={`w-6 h-6 ${s.check} rounded-full flex items-center justify-center flex-shrink-0`}>
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-wrap gap-6 mb-8 p-4 bg-white/50 rounded-2xl">
              <div className="flex items-center gap-2 text-slate-700">
                <Clock className="w-5 h-5 text-yellow-600" />
                <span>{program.schedule}</span>
              </div>
            </div>
            <div className="mb-8 p-4 bg-yellow-100 border border-yellow-300 rounded-2xl">
              <p className="text-slate-800 text-sm">
                <strong>Payment Information:</strong> For payment arrangements, please contact our administration directly at <a href="tel:+15715359999" className="text-yellow-700 font-semibold hover:underline">(571) 535-9999</a>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/book-visit">
                <Button className="btn-playful" data-testid={`enroll-btn-${program.id}`}>
                  Schedule a Tour
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:+15715359999">
                <Button variant="outline" data-testid={`call-btn-${program.id}`} className="rounded-full px-6 py-4 font-bold border-2 border-slate-300 hover:border-yellow-400">
                  <Phone className="mr-2 w-4 h-4" />
                  Call Us
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const PreschoolCurriculum = () => (
  <section className="py-20" data-testid="preschool-curriculum-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center max-w-3xl mx-auto mb-14">
        <span className="inline-block bg-sky-100 text-sky-700 px-4 py-2 rounded-full text-sm font-medium mb-4">Preschool Curriculum | Ages 4–5</span>
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">What Your Preschooler Will Learn</h2>
        <p className="text-slate-600">Fun, age-appropriate activities that build essential skills and prepare children for kindergarten.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
        {CURRICULUM.map((c, i) => (
          <Card key={c.title} className={`border-2 rounded-3xl card-hover ${c.color.split(" ").pop()}`} data-testid={`curriculum-card-${i + 1}`}>
            <CardContent className="p-7">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${c.color}`}>
                  <c.icon className="w-6 h-6" />
                </div>
                <span className="text-4xl font-bold text-slate-200">{i + 1}</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{c.title}</h3>
              <p className="text-slate-600 leading-relaxed">{c.text}</p>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid md:grid-cols-2 gap-6 mt-10">
        <Card className="bg-white border-2 border-slate-200 rounded-3xl" data-testid="teaching-methods-card">
          <CardContent className="p-7">
            <h3 className="text-xl font-bold text-slate-900 mb-2 flex items-center gap-2"><BookOpen className="w-5 h-5 text-pink-500" />Teaching Methods</h3>
            <p className="text-slate-600">We use stories, educational games, hands-on activities, creative play, and group learning to make learning enjoyable.</p>
          </CardContent>
        </Card>
        <Card className="bg-sky-500 border-0 rounded-3xl text-white" data-testid="preschool-goal-card">
          <CardContent className="p-7">
            <h3 className="text-xl font-bold mb-2 flex items-center gap-2"><Target className="w-5 h-5" />Our Goal</h3>
            <p className="text-sky-50">To help every child learn, grow, build confidence, and develop the skills needed for a successful transition to kindergarten.</p>
            <p className="mt-3 font-bold text-yellow-200">Learn, Grow, and Thrive!</p>
          </CardContent>
        </Card>
      </div>
    </div>
  </section>
);

export default function ProgramsPage() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/programs`)
      .then((res) => setPrograms(res.data))
      .catch((error) => console.error("Error fetching programs:", error))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="noise-bg min-h-screen" data-testid="programs-page">
      <section className="pt-32 pb-16 lg:pt-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto animate-fade-in">
            <span className="inline-block bg-yellow-100 text-yellow-700 px-4 py-2 rounded-full text-sm font-medium mb-6">Our Programs</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">Toddler & Preschool Programs</h1>
            <p className="text-lg text-slate-600">
              From curious toddlers to kindergarten-ready preschoolers, our programs support every child's growth in a safe, loving environment.
            </p>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {programs.map((program, i) => (
            <ProgramDetail key={program.id} program={program} reverse={i % 2 === 1} />
          ))}
        </div>
      </section>

      <PreschoolCurriculum />

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-yellow-400 border-0 rounded-3xl overflow-hidden">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Ready to Learn More?</h2>
              <p className="text-slate-800 text-lg mb-8 max-w-xl mx-auto">
                Schedule a visit and see how our toddler and preschool programs can help your child grow and thrive!
              </p>
              <Link to="/book-visit">
                <Button className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-4 text-base font-bold shadow-lg" data-testid="programs-cta-btn">
                  Book a Tour Today
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
